import {
  Alert,
  Box,
  Button,
  Link,
  SpaceBetween,
  TextContent,
} from "@amzn/awsui-components-console";
import React from "react";

import { ROUTES } from "src/app/constants";
import { LOB_LABELS } from "src/jsonForms/preScreen/lobAccess";
import type { LobKey } from "src/jsonForms/preScreen/types";

import type { SearchConfigFailure, SearchConfigRetrievalCause } from "./searchConfigResolver";

export interface ConfigErrorPanelProps {
  readonly failure: SearchConfigFailure;
  /** Offered only on a retrieval failure, the one kind another attempt can clear. */
  readonly onRetry?: () => void;
  /** Disables the retry control while the retrieval it issued is in flight. */
  readonly retrying?: boolean;
}

function unknownRouteMessage(pathname: string): string {
  const intro = `No line of business is registered for the path "${pathname}".`;
  return `${intro} There is no claims history to show for it.`;
}

function unavailableMessage(lob: LobKey): string {
  return `The claims history configuration for ${LOB_LABELS[lob]} is unavailable.`;
}

function rejectedMessage(lob: LobKey): string {
  const label = LOB_LABELS[lob];
  return `The claims history configuration for ${label} was rejected for the following reasons:`;
}

/**
 * Separate from `unavailableMessage` because the two are diagnosed differently:
 * a missing config is a configuration gap, and this is a config that exists but
 * could not be fetched. Worded per cause so a report of what the user saw says
 * which one happened.
 */
function retrievalMessage(lob: LobKey, cause: SearchConfigRetrievalCause): string {
  const subject = `The claims history configuration for ${LOB_LABELS[lob]}`;
  switch (cause) {
    case "timeout":
      return `${subject} took too long to load.`;
    case "unparseable":
      return `${subject} was returned in a form that could not be read.`;
    case "transport":
      return `${subject} could not be loaded.`;
  }
}

interface RetryButtonProps {
  readonly onRetry: () => void;
  readonly retrying: boolean;
}

/** No cap on activations: each one is a single further retrieval. */
function RetryButton({ onRetry, retrying }: RetryButtonProps): React.ReactElement {
  return (
    <Button
      variant="primary"
      onClick={onRetry}
      disabled={retrying}
      loading={retrying}
      data-testid="config-error-retry"
    >
      Try again
    </Button>
  );
}

/**
 * An in-app link rather than a router link, matching how the landing page and
 * the error pages already link across the application.
 */
function LandingPageLink(): React.ReactElement {
  return (
    <Link
      href={ROUTES.LANDING_PAGE}
      variant="primary"
      ariaLabel="Go to the landing page to choose another line of business"
    >
      Go to the landing page
    </Link>
  );
}

/**
 * The configuration failure surface, rendered in place of the search bar and the
 * table. A page showing this panel has issued no claims request and holds no
 * control that could issue one.
 *
 * Only a retrieval failure offers a retry. The others are deterministic — an
 * unregistered route, no stored document, a document the validator rejects —
 * so a second attempt returns the same answer, and a control offering one would
 * replay the failure while looking as though it had done something.
 */
function ConfigErrorPanel({
  failure,
  onRetry,
  retrying = false,
}: ConfigErrorPanelProps): React.ReactElement {
  switch (failure.kind) {
    case "unknownRoute":
      return (
        <Alert
          type="error"
          header="Claims history is unavailable for this page"
          statusIconAriaLabel="Error"
          data-testid="config-error-unknown-route"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{unknownRouteMessage(failure.pathname)}</Box>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "missingConfig":
      return (
        <Alert
          type="error"
          header="Claims history configuration unavailable"
          statusIconAriaLabel="Error"
          data-testid="config-error-missing-config"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{unavailableMessage(failure.lob)}</Box>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "invalidConfig":
      return (
        <Alert
          type="error"
          header="Claims history configuration could not be used"
          statusIconAriaLabel="Error"
          data-testid="config-error-invalid-config"
        >
          <SpaceBetween direction="vertical" size="s">
            <TextContent>
              <div>{rejectedMessage(failure.lob)}</div>
              <ul data-testid="config-error-validation-list">
                {failure.errors.map((error, index) => (
                  <li key={`${error.path}-${index}`}>
                    <b>{`${error.path}: `}</b>
                    {error.message}
                  </li>
                ))}
              </ul>
            </TextContent>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "retrieval":
      return (
        <Alert
          type="error"
          header="Claims history configuration unavailable"
          statusIconAriaLabel="Error"
          data-testid="config-error-retrieval"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{retrievalMessage(failure.lob, failure.cause)}</Box>
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              {onRetry && <RetryButton onRetry={onRetry} retrying={retrying} />}
              <LandingPageLink />
            </SpaceBetween>
          </SpaceBetween>
        </Alert>
      );
  }
}

export default ConfigErrorPanel;
